import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db, ensureSessionPersistence, getAuthClient } from '../../firebaseConfig';
import { useAuth } from '../../auth/AuthProvider';
import { resolveOnTimeSessionForUser, writeOnTimeSession } from '../auth/session';
import { hasOnTimeAccess, normalizeOnTimeRole } from '../utils/access';
import { useOnTimeLiveData } from '../hooks/useOnTimeLiveData';
import { driverRequestRun, driverStartRun, markDeliveredWithProof, updateDriverLocation, setDriverAvailability, storeCancelOrder } from '../lib/actions';
import '../styles/linkPwa.css';

const DRIVER_SESSION_KEY = 'matrixos.link.driverPwa.session.v1';
const DRIVER_ONLINE_KEY = 'matrixos.link.driverPwa.online.v1';
const DRIVER_ONLINE_GLOBAL_KEY = 'matrixos.link.driverPwa.online.current.v1';

function cleanPin(value = '') {
  return String(value || '').replace(/\D/g, '').slice(0, 6);
}

function profileName(profile = {}) {
  return String(profile.displayName || `${profile.fullName || ''} ${profile.surname || ''}`.trim() || profile.name || profile.email || 'Driver').trim();
}

function profilePin(profile = {}) {
  return cleanPin(profile.accessPin || profile.pin || profile.linkPin || profile.onTimePin || '');
}

function isActive(profile = {}) {
  return profile.active !== false && profile.disabled !== true && profile.archived !== true && profile.removed !== true;
}

function canUseAsDriverProfile(profile = {}) {
  const role = normalizeOnTimeRole(profile.onTimeRole || profile.role || '');
  const roleText = String(profile.onTimeRole || profile.role || '').toLowerCase();
  return isActive(profile) && profilePin(profile).length === 6 && (role === 'driver' || roleText.includes('driver'));
}

function getProfileSites(profile = {}) {
  const raw = profile.assignedStores || profile.stores || profile.storeAccess || profile.storeNames || profile.sites || profile.siteAccess || profile.siteNames || profile.store || profile.site || [];
  if (Array.isArray(raw)) return Array.from(new Set(raw.map((x) => String(x || '').trim()).filter(Boolean))).sort();
  const single = String(raw || '').trim();
  return single ? [single] : [];
}

function readDriverSession() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(DRIVER_SESSION_KEY) || window.sessionStorage.getItem(DRIVER_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null;
  }
}

function writeDriverSession(session) {
  if (typeof window === 'undefined') return;
  try { const payload = JSON.stringify({ ...session, savedAt: Date.now() }); window.localStorage.setItem(DRIVER_SESSION_KEY, payload); window.sessionStorage.setItem(DRIVER_SESSION_KEY, payload); } catch (_) {}
}


function driverOnlineKey(session = {}) {
  return `${DRIVER_ONLINE_KEY}:${session.orgId || ''}:${session.profileId || ''}`;
}

function storedDriverOnlineValue(session = {}) {
  if (typeof window === 'undefined' || !session?.profileId) return null;
  try {
    const keyed = window.localStorage.getItem(driverOnlineKey(session));
    if (keyed === 'true' || keyed === 'false') return keyed === 'true';
    const global = window.localStorage.getItem(`${DRIVER_ONLINE_GLOBAL_KEY}:${session.profileId}`);
    if (global === 'true' || global === 'false') return global === 'true';
  } catch (_) {}
  return null;
}

function readDriverOnline(session = {}) {
  const stored = storedDriverOnlineValue(session);
  if (stored !== null) return stored;
  return Boolean(session?.driverOnline);
}

function writeDriverOnline(session = {}, online = false) {
  if (typeof window === 'undefined' || !session?.profileId) return;
  try {
    window.localStorage.setItem(driverOnlineKey(session), online ? 'true' : 'false');
    window.localStorage.setItem(`${DRIVER_ONLINE_GLOBAL_KEY}:${session.profileId}`, online ? 'true' : 'false');
    const current = readDriverSession();
    if (String(current?.profileId || '') === String(session.profileId || '')) {
      const payload = JSON.stringify({ ...current, driverOnline: Boolean(online), savedAt: Date.now() });
      window.localStorage.setItem(DRIVER_SESSION_KEY, payload);
      window.sessionStorage.setItem(DRIVER_SESSION_KEY, payload);
    }
  } catch (_) {}
}

function clearDriverSession() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(DRIVER_SESSION_KEY);
    window.localStorage.removeItem(DRIVER_SESSION_KEY);
  } catch (_) {}
}

function buildMapsUrl(address = '', origin = null) {
  const destination = encodeURIComponent(String(address || '').trim());
  if (!destination) return '';
  const originPart = origin?.latitude && origin?.longitude ? `&origin=${origin.latitude},${origin.longitude}` : '';
  return `https://www.google.com/maps/dir/?api=1${originPart}&destination=${destination}&travelmode=driving`;
}

function buildMapsEmbedUrl(address = '', origin = null) {
  const destination = encodeURIComponent(String(address || '').trim());
  if (!destination) return '';
  const originText = origin?.latitude && origin?.longitude ? `${origin.latitude},${origin.longitude}` : 'Current Location';
  return `https://maps.google.com/maps?saddr=${encodeURIComponent(originText)}&daddr=${destination}&output=embed`;
}

function getBrowserPosition() {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy || null }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 9000, maximumAge: 30000 }
    );
  });
}

function LinkLogo({ small = false }) {
  return <div className={`link-pwa-logo ${small ? 'small' : ''}`}>Link<span>.</span></div>;
}

function useLinkDriverInstall() {
  const [promptEvent, setPromptEvent] = useState(null);
  const [installed, setInstalled] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    try {
      document.title = 'Link Driver App';
      const manifests = Array.from(document.querySelectorAll('link[rel="manifest"]'));
      if (manifests.length) {
        manifests.forEach((link) => {
          link.setAttribute('data-previous-href', link.getAttribute('href') || '');
          link.setAttribute('data-link-driver-manifest', 'true');
          link.setAttribute('href', '/link-driver-manifest.webmanifest?v=1');
        });
      } else {
        const link = document.createElement('link');
        link.rel = 'manifest';
        link.href = '/link-driver-manifest.webmanifest?v=1';
        link.setAttribute('data-link-driver-manifest', 'true');
        document.head.appendChild(link);
      }
      const theme = document.querySelector('meta[name="theme-color"]') || document.createElement('meta');
      theme.setAttribute('name', 'theme-color');
      theme.setAttribute('content', '#020817');
      if (!theme.parentNode) document.head.appendChild(theme);
    } catch (_) {}
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/link-driver-sw.js', { scope: '/' }).catch(() => {});
    }
    const onBeforeInstall = (event) => { event.preventDefault(); setPromptEvent(event); };
    const onInstalled = () => { setInstalled(true); setPromptEvent(null); };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    if (window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator?.standalone) setInstalled(true);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);
  const install = useCallback(async () => {
    if (!promptEvent) return false;
    promptEvent.prompt();
    await promptEvent.userChoice.catch(() => null);
    setPromptEvent(null);
    return true;
  }, [promptEvent]);
  return { canInstall: Boolean(promptEvent), installed, install };
}

function InstallDriverPwaButton({ compact = false }) {
  const { canInstall, installed, install } = useLinkDriverInstall();
  const [hint, setHint] = useState('');
  if (installed) return <span className={`link-pwa-install-pill ${compact ? 'compact' : ''}`}>Installed</span>;
  return <button type="button" className={`link-pwa-install-btn ${compact ? 'compact' : ''}`} onClick={async () => {
    if (canInstall) { await install(); return; }
    setHint('Install prompt not available yet. Use Chrome menu → Add to Home screen after this page finishes loading.');
    window.setTimeout(() => setHint(''), 3500);
  }}>Install app{hint ? <small>{hint}</small> : null}</button>;
}

function money(n) {
  return `R ${Number(n || 0).toFixed(0)}`;
}

function safeTime(raw) {
  if (!raw) return 0;
  if (raw?.toDate) return raw.toDate().getTime();
  if (raw instanceof Date) return raw.getTime();
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function minsSince(raw) {
  const start = safeTime(raw) || Date.now();
  return Math.max(0, Math.floor((Date.now() - start) / 60000));
}

function fmtMins(mins) {
  const h = Math.floor(Number(mins || 0) / 60);
  const m = Number(mins || 0) % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
}

function formatClock(raw) {
  const ms = safeTime(raw);
  if (!ms) return '—';
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function readyWaitLabel(order = {}) {
  const start = readyTime(order);
  return fmtMins(minsSince(start));
}

function hasStoreCollectedProof(order = {}) {
  const raw = order.raw || {};
  const t = order.timings || raw.timings || {};
  const stage = String(order.stage || raw.stage || '').toLowerCase();
  const storeStatus = String(order.storeStatus || raw.storeStatus || order.fulfilment?.storeStatus || raw.fulfilment?.storeStatus || '').toLowerCase();
  const handoff = order.assignment?.siteHandedToDriver || raw.assignment?.siteHandedToDriver;
  const proofBuckets = [order.proof, raw.proof, order.storeProof, raw.storeProof, order.photos, raw.photos, order.evidence, raw.evidence, order.attachments, raw.attachments].filter(Boolean);
  const proofText = JSON.stringify(proofBuckets).toLowerCase();
  return Boolean(
    t.collectedAt || t.handoverAt || t.handedToDriverAt || handoff ||
    storeStatus === 'collected' || stage === 'collected' || stage === 'out_for_delivery' ||
    proofText.includes('collectedphotourl') || proofText.includes('handoverphotourl') || proofText.includes('handoff') ||
    order?.proof?.collectedPhotoUrl || raw?.proof?.collectedPhotoUrl ||
    order?.storeProof?.collectedPhotoUrl || raw?.storeProof?.collectedPhotoUrl ||
    order?.proof?.handoverPhotoUrl || raw?.proof?.handoverPhotoUrl ||
    order?.storeProof?.handoverPhotoUrl || raw?.storeProof?.handoverPhotoUrl
  );
}


function startOfDay(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date = new Date()) {
  const d = startOfDay(date);
  d.setDate(d.getDate() + 1);
  d.setMilliseconds(-1);
  return d;
}

function startOfWeek(date = new Date()) {
  const d = startOfDay(date);
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1);
  return d;
}

function periodRange(period = 'today', customDate = '') {
  const now = new Date();
  if (period === 'yesterday') {
    const d = startOfDay(now);
    d.setDate(d.getDate() - 1);
    return { start: d.getTime(), end: endOfDay(d).getTime(), label: 'Yesterday' };
  }
  if (period === 'thisWeek') {
    return { start: startOfWeek(now).getTime(), end: now.getTime(), label: 'Current week' };
  }
  if (period === 'lastWeek') {
    const start = startOfWeek(now);
    start.setDate(start.getDate() - 7);
    const end = new Date(start);
    end.setDate(end.getDate() + 7);
    end.setMilliseconds(-1);
    return { start: start.getTime(), end: end.getTime(), label: 'Last week' };
  }
  if (period === 'monthToDate') {
    const start = startOfDay(now);
    start.setDate(1);
    return { start: start.getTime(), end: now.getTime(), label: 'Month to date' };
  }
  if (period === 'specific') {
    const chosen = customDate ? new Date(`${customDate}T00:00:00`) : now;
    return { start: startOfDay(chosen).getTime(), end: endOfDay(chosen).getTime(), label: customDate || 'Specific date' };
  }
  return { start: startOfDay(now).getTime(), end: now.getTime(), label: 'Today' };
}

function isInRange(raw, range) {
  const t = safeTime(raw);
  return Boolean(t && t >= range.start && t <= range.end);
}

function readyTime(order = {}) {
  const t = order.timings || order.raw?.timings || {};
  return t.readyForDriverAt || t.readyAt || t.packedAt || order.updatedAt || order.createdAt;
}

function acceptedTime(order = {}) {
  const t = order.timings || order.raw?.timings || {};
  return t.driverAcceptedAt || t.acceptedAt || t.pickedUpAt || order.raw?.driverAcceptedAt || order.updatedAt;
}

function deliveredTime(order = {}) {
  const t = order.timings || order.raw?.timings || {};
  return t.deliveredAt || t.completedAt || order.raw?.deliveredAt || order.raw?.completedAt || order.updatedAt;
}

function cancelledTime(order = {}) {
  const t = order.timings || order.raw?.timings || {};
  return t.cancelledAt || t.canceledAt || order.raw?.cancelledAt || order.raw?.canceledAt || order.updatedAt;
}

function isCancelledOrder(order = {}) {
  const stage = String(order.stage || order.status || order.raw?.stage || order.raw?.status || '').toLowerCase();
  return stage === 'cancelled' || stage === 'canceled';
}

function minutesBetween(startRaw, endRaw) {
  const start = safeTime(startRaw);
  const end = safeTime(endRaw);
  if (!start || !end || end < start) return null;
  return Math.max(0, Math.floor((end - start) / 60000));
}


function clampScore(value) {
  return Math.max(0, Math.min(100, Math.round(Number(value) || 0)));
}

function driverDeliveryMinutes(order = {}) {
  return minutesBetween(acceptedTime(order), deliveredTime(order));
}

function readyWaitMinutes(order = {}) {
  const end = acceptedTime(order) || deliveredTime(order) || order.updatedAt;
  return minutesBetween(readyTime(order), end);
}

function buildDriverIntelligence({ rows, deliveredInSelectedPeriod, driverOverviewRange, session }) {
  const periodOrders = (rows.scoped || rows.visible || []).filter((order) => isInRange(readyTime(order), driverOverviewRange) || isInRange(acceptedTime(order), driverOverviewRange) || isInRange(deliveredTime(order), driverOverviewRange));
  const acceptedByMe = periodOrders.filter((order) => isAssignedTo(order, session));
  const cancelledByMe = rows.visible.filter((order) => isAssignedTo(order, session) && isCancelledOrder(order) && isInRange(cancelledTime(order), driverOverviewRange));
  const deliveryDurations = deliveredInSelectedPeriod.map(driverDeliveryMinutes).filter((value) => Number.isFinite(value));
  const acceptanceWaits = acceptedByMe.map(readyWaitMinutes).filter((value) => Number.isFinite(value));
  const avgDelivery = deliveryDurations.length ? Math.round(deliveryDurations.reduce((sum, value) => sum + value, 0) / deliveryDurations.length) : 0;
  const avgAcceptance = acceptanceWaits.length ? Math.round(acceptanceWaits.reduce((sum, value) => sum + value, 0) / acceptanceWaits.length) : 0;
  const totalReady = periodOrders.filter((order) => readyForDriver(order) || isAssignedTo(order, session) || String(order.stage || order.raw?.stage || '').toLowerCase() === 'delivered').length;
  const acceptedCount = acceptedByMe.length;
  const acceptanceRate = totalReady ? Math.round((acceptedCount / totalReady) * 100) : 0;
  const lateDeliveries = deliveryDurations.filter((mins) => mins > 45).length;
  const slowAccepts = acceptanceWaits.filter((mins) => mins > 10).length;
  const completionReliability = acceptedCount ? Math.round((deliveredInSelectedPeriod.length / acceptedCount) * 100) : (deliveredInSelectedPeriod.length ? 100 : 0);
  const proofCompliant = deliveredInSelectedPeriod.filter((order) => Boolean(order.proof?.deliveredPhotoUrl || order.driverProof?.deliveredPhotoUrl || order.raw?.proof?.deliveredPhotoUrl || order.raw?.driverProof?.deliveredPhotoUrl)).length;
  const proofRate = deliveredInSelectedPeriod.length ? Math.round((proofCompliant / deliveredInSelectedPeriod.length) * 100) : 0;
  const onlineScore = acceptedCount || deliveredInSelectedPeriod.length ? 100 : 0;
  const score = clampScore((acceptanceRate * 0.28) + (completionReliability * 0.32) + ((avgAcceptance ? Math.max(0, 100 - avgAcceptance * 4) : 80) * 0.18) + (proofRate * 0.12) + (Math.max(0, 100 - lateDeliveries * 18 - slowAccepts * 8) * 0.10));
  const risks = [];
  if (slowAccepts) risks.push(`${slowAccepts} slow acceptance${slowAccepts === 1 ? '' : 's'}`);
  if (lateDeliveries) risks.push(`${lateDeliveries} late delivery ${lateDeliveries === 1 ? 'risk' : 'risks'}`);
  if (cancelledByMe.length) risks.push(`${cancelledByMe.length} cancelled`);
  if (deliveredInSelectedPeriod.length && proofRate < 100) risks.push('proof gap');
  return {
    range: driverOverviewRange,
    totalReady,
    acceptedCount,
    rate: acceptanceRate,
    deliveredCount: deliveredInSelectedPeriod.length,
    cancelledCount: cancelledByMe.length,
    avgDelivery,
    avgAcceptance,
    completionReliability,
    proofRate,
    onlineScore,
    score,
    lateDeliveries,
    slowAccepts,
    riskSummary: risks.length ? risks.join(' · ') : 'No driver risks detected in this period',
  };
}

function orderRef(order = {}) {
  return `#${order.orderNumber || order.id || 'Order'}`;
}

function storeName(order = {}) {
  return String(order.store || order.storeName || order.assignedStore || order.raw?.storeName || order.raw?.store || 'Unassigned site');
}

function customerName(order = {}) {
  return String(order.customerName || order.raw?.customer?.name || 'Customer');
}

function finalTotal(order = {}) {
  const raw = order.raw || {};
  return Number(raw.swapAdjustedTotal ?? raw.finalTotal ?? raw.pricing?.total ?? order.total ?? 0);
}

function isClosedDriverOrder(order = {}) {
  const raw = order.raw || {};
  const stage = String(order.stage || raw.stage || '').toLowerCase();
  const status = String(order.status || raw.status || '').toLowerCase();
  const storeStatus = String(order.storeStatus || raw.storeStatus || order.fulfilment?.storeStatus || raw.fulfilment?.storeStatus || '').toLowerCase();
  const driverStatus = String(order.driverStatus || raw.driverStatus || '').toLowerCase();
  const closed = new Set(['archived', 'cancelled', 'canceled', 'delivered', 'completed', 'complete']);
  return Boolean(
    order.archived || raw.archived || order.cancelled || raw.cancelled || order.archivedAt || raw.archivedAt ||
    closed.has(stage) || closed.has(status) || closed.has(storeStatus) || closed.has(driverStatus)
  );
}


function myRunMins(order = {}) {
  const t = order.timings || order.raw?.timings || {};
  return minsSince(t.driverAcceptedAt || t.acceptedAt || t.pickedUpAt || order.updatedAt || order.createdAt);
}

function readyForDriver(order = {}) {
  const raw = order.raw || {};
  const stage = String(order.stage || raw.stage || '').toLowerCase();
  const status = String(order.status || raw.status || '').toLowerCase();
  const storeStatus = String(order.storeStatus || order.fulfilment?.storeStatus || raw.storeStatus || raw.fulfilment?.storeStatus || '').toLowerCase();
  const mode = String(order.deliveryMode || order.handoffMode || order.assignment?.deliveryMode || order.assignment?.handoffMode || raw.deliveryMode || raw.handoffMode || raw.assignment?.deliveryMode || raw.assignment?.handoffMode || '').toLowerCase();
  if (isClosedDriverOrder(order)) return false;
  if (mode.includes('other') || mode.includes('uber')) return false;
  if (stage === 'out_for_delivery') return false;
  const readyStates = new Set(['ready', 'ready_for_driver', 'awaiting_driver', 'collected']);
  return Boolean(
    hasStoreCollectedProof(order) ||
    readyStates.has(stage) ||
    readyStates.has(status) ||
    readyStates.has(storeStatus)
  );
}

function assignmentValues(order = {}) {
  const raw = order.raw || {};
  const assignment = order.assignment || raw.assignment || {};
  return [
    order.assignedDriver, order.assignedDriverName, order.driverName, order.driverId,
    raw.assignedDriver, raw.assignedDriverName, raw.driverName, raw.driverId,
    assignment.driverId, assignment.driverName, assignment.selectedDriverId, assignment.selectedDriverName,
    assignment.siteSelectedDriver, order.selectedDriver, order.selectedDriverName,
    raw.selectedDriver, raw.selectedDriverName,
  ].filter(Boolean).map((x) => String(x).trim().toLowerCase());
}

function driverIdentityValues(session = {}) {
  return [session.profileId, session.profileName, session.email].filter(Boolean).map((x) => String(x).trim().toLowerCase());
}

function isAssignedTo(order = {}, session = {}) {
  const values = assignmentValues(order);
  const mine = driverIdentityValues(session);
  return mine.some((x) => values.includes(x));
}


function driverRequestList(order = {}) {
  const raw = order.raw || {};
  const rows = order.driverRequests || raw.driverRequests || [];
  return Array.isArray(rows) ? rows : [];
}

function hasDriverRequested(order = {}, session = {}) {
  const raw = order.raw || {};
  const ids = order.driverRequestIds || raw.driverRequestIds || [];
  const idHit = Array.isArray(ids) && ids.map(String).includes(String(session.profileId || ''));
  const requestHit = driverRequestList(order).some((row) => String(row?.driverId || '').toLowerCase() === String(session.profileId || '').toLowerCase() || String(row?.driverName || '').toLowerCase() === String(session.profileName || '').toLowerCase());
  return Boolean(idHit || requestHit);
}

function isAssignedToOther(order = {}, session = {}) {
  const assigned = assignmentValues(order);
  if (!assigned.length) return false;
  const mine = driverIdentityValues(session);
  return !mine.some((x) => assigned.includes(x));
}

function step(order = {}, session = {}) {
  const raw = order.raw || {};
  const stage = String(order.stage || raw.stage || '').toLowerCase();
  const t = order.timings || raw.timings || {};
  const siteCollected = hasStoreCollectedProof(order);
  if (isClosedDriverOrder(order)) return 'closed';
  if (stage === 'delivered' || t.deliveredAt) return 'delivered';
  if (stage === 'out_for_delivery' || t.outForDeliveryAt) return 'delivering';
  if (isAssignedTo(order, session)) return siteCollected ? 'selected' : 'selected_waiting_collection';
  // Safety: if this driver requested the run and the store has already completed the handover,
  // let the driver proceed even if older records missed the assignment mirror field.
  if (hasDriverRequested(order, session) && siteCollected && !isAssignedToOther(order, session)) return 'selected';
  if (hasDriverRequested(order, session)) return 'requested';
  return 'ready';
}

function actorFor(session = {}, user = null) {
  return {
    app: 'link-driver',
    role: 'driver',
    userId: session.profileId || user?.uid || '',
    name: session.profileName || user?.displayName || user?.email || 'Driver',
    email: session.email || user?.email || '',
  };
}

function DriverEmailLogin({ onReady }) {
  const { refreshClaims } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setMessage('');
    clearDriverSession();
    try {
      await ensureSessionPersistence?.();
      const auth = getAuthClient();
      const result = await signInWithEmailAndPassword(auth, email.trim(), password);
      const session = await resolveOnTimeSessionForUser(result.user);
      if (!session?.orgId || !hasOnTimeAccess(session?.claims || {})) {
        await signOut(auth);
        setMessage('This email does not have Link access. Ask your MatrixOS admin to activate Link.');
        return;
      }
      writeOnTimeSession(session);
      await refreshClaims?.({ force: true });
      onReady?.();
    } catch (error) {
      console.error('[Link Driver] login failed', error);
      setMessage('Email/password failed, or Link access is not active for this user.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="link-pwa-screen link-driver-pwa link-pwa-centered">
      <form className="link-pwa-login-card link-driver-login" onSubmit={submit}>
        <LinkLogo />
        <div className="link-pwa-kicker">Driver App · MatrixOS Entry</div>
        <h1>Sign into Link Driver.</h1>
        <p>Use your MatrixOS email first. Then unlock the driver board with the driver PIN.</p>
        <label>Email</label>
        <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="driver@example.com" autoComplete="username" required />
        <label>Password</label>
        <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" placeholder="Password" autoComplete="current-password" required />
        {message ? <div className="link-pwa-alert">{message}</div> : null}
        <button className="link-pwa-primary-wide" type="submit" disabled={busy}>{busy ? 'Signing in…' : 'Sign in to Driver App'}</button>
      </form>
    </div>
  );
}

function DriverProfileGate({ onUnlock }) {
  const { user, claims } = useAuth();
  const orgId = String(claims?.orgId || '').trim();
  const [profiles, setProfiles] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [profileSearch, setProfileSearch] = useState('');
  const [pin, setPin] = useState('');
  const [selectedSite, setSelectedSite] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const selected = useMemo(() => profiles.find((p) => String(p.id) === String(selectedId)) || null, [profiles, selectedId]);
  const selectedSites = useMemo(() => getProfileSites(selected || {}), [selected]);
  const filteredProfiles = useMemo(() => {
    const needle = profileSearch.trim().toLowerCase();
    if (!needle) return profiles;
    return profiles.filter((profile) => {
      const hay = [profileName(profile), profile.email, profile.phone, profile.role, profile.onTimeRole, ...getProfileSites(profile)].join(' ').toLowerCase();
      return hay.includes(needle);
    });
  }, [profiles, profileSearch]);

  const loadProfiles = useCallback(async () => {
    if (!orgId) return;
    setBusy(true);
    setError('');
    try {
      const snap = await getDocs(collection(db, 'orgs', orgId, 'linkProfiles'));
      const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })).filter(canUseAsDriverProfile);
      setProfiles(rows);
      setProfileSearch('');
      if (rows[0]) { setSelectedId(rows[0].id); const firstSites = getProfileSites(rows[0]); setSelectedSite(firstSites[0] || ''); }
    } catch (err) {
      console.error('[Link Driver] profile load failed', err);
      setError('Could not load driver profiles. Check Link Profiles and Firestore rules.');
    } finally {
      setBusy(false);
    }
  }, [orgId]);

  useEffect(() => { loadProfiles(); }, [loadProfiles]);

  useEffect(() => {
    setProfileSearch('');
    setPin('');
    const t = setTimeout(() => { setProfileSearch(''); setPin(''); }, 150);
    return () => clearTimeout(t);
  }, [user?.uid, orgId]);

  useEffect(() => {
    const sites = selectedSites || [];
    if (sites.length && !sites.includes(selectedSite)) setSelectedSite(sites[0]);
    if (!sites.length && selectedSite) setSelectedSite('');
  }, [selectedId, selectedSites, selectedSite]);

  async function unlock(e) {
    e.preventDefault();
    setError('');
    if (!selected) return setError('No active Driver profiles found. Create one in Link Admin → Profiles.');
    if (selectedSites.length > 1 && !selectedSite) return setError('Choose which site this driver is working from.');
    if (cleanPin(pin) !== profilePin(selected)) return setError('Incorrect PIN for this driver profile.');
    const session = {
      app: 'link-driver',
      orgId,
      uid: user?.uid || '',
      email: user?.email || '',
      profileId: selected.id,
      profileName: profileName(selected),
      sites: getProfileSites(selected),
      selectedSite: selectedSite || getProfileSites(selected)[0] || '',
      role: 'driver',
    };
    writeDriverSession(session);
    onUnlock(session);
  }

  async function logout() {
    clearDriverSession();
    try { await signOut(getAuthClient()); } catch (_) {}
  }

  return (
    <div className="link-pwa-screen link-driver-pwa link-pwa-centered">
      <form className="link-pwa-login-card link-pwa-unlock-flow" onSubmit={unlock} autoComplete="off">
        <input className="link-hidden-autofill" type="text" autoComplete="username" tabIndex="-1" aria-hidden="true" />
        <input className="link-hidden-autofill" type="password" autoComplete="current-password" tabIndex="-1" aria-hidden="true" />
        <div className="link-pwa-login-topline">
          <div><LinkLogo small /><div className="link-pwa-kicker">Driver Access Layer</div></div>
          <button type="button" className="link-pwa-ghost" onClick={logout}>Logout</button>
        </div>
        <h1>Choose driver profile.</h1>
        <label>Driver profile</label>
        <select className="link-driver-site-select" value={selectedId} onChange={(e) => { const id = e.target.value; setSelectedId(id); const profile = profiles.find((p) => String(p.id) === String(id)); const sites = getProfileSites(profile || {}); setSelectedSite(sites[0] || ''); }}>
          {profiles.map((profile) => <option key={profile.id} value={profile.id}>{profileName(profile)} · {getProfileSites(profile).join(', ') || 'All assigned sites'}</option>)}
        </select>
        <label>Search driver profile</label>
        <input name={`link-driver-profile-search-${orgId || 'org'}`} value={profileSearch} onChange={(e) => setProfileSearch(e.target.value)} placeholder="Search driver name or site" autoComplete="off" autoCorrect="off" spellCheck="false" />
        {profileSearch.trim() ? (
          <div className="link-driver-profile-results">
            {filteredProfiles.length ? filteredProfiles.map((profile) => {
              const active = String(profile.id) === String(selectedId);
              return (
                <button type="button" key={profile.id} className={`link-driver-profile-row ${active ? 'active' : ''}`} onClick={() => { setSelectedId(profile.id); const sites = getProfileSites(profile); setSelectedSite(sites[0] || ''); setProfileSearch(''); }}>
                  <span>{profileName(profile).split(' ').map((x) => x[0]).join('').slice(0, 2).toUpperCase() || 'DR'}</span>
                  <b>{profileName(profile)}</b>
                  <small>Driver · {getProfileSites(profile).join(', ') || 'All assigned sites'}</small>
                </button>
              );
            }) : <div className="link-pwa-soft-note">No driver profiles match that search.</div>}
          </div>
        ) : null}
        {selectedSites.length > 1 ? (
          <>
            <label>Choose driver site</label>
            <select className="link-driver-site-select" value={selectedSite} onChange={(e) => setSelectedSite(e.target.value)}>
              {selectedSites.map((site) => <option key={site} value={site}>{site}</option>)}
            </select>
          </>
        ) : null}
        <label>PIN</label>
        <input name={`link-driver-pin-${selectedId || 'none'}`} value={pin} onChange={(e) => setPin(cleanPin(e.target.value))} inputMode="numeric" type="password" placeholder="6-digit PIN" autoComplete="new-password" maxLength="6" />
        {selectedSites.length && selectedSites.length <= 1 ? <div className="link-pwa-soft-note">Routes: {selectedSites.join(', ') || 'All assigned sites'}</div> : null}
        {error ? <div className="link-pwa-alert">{error}</div> : null}
        <button className="link-pwa-primary-wide" disabled={busy || !selected || cleanPin(pin).length !== 6}>{busy ? 'Loading…' : 'Enter Driver Board'}</button>
      </form>
    </div>
  );
}


function SignaturePad({ value, onChange }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const width = Math.max(1, Math.floor(rect.width * dpr));
    const height = Math.max(1, Math.floor(rect.height * dpr));
    if (canvas.width !== width || canvas.height !== height) {
      const old = canvas.toDataURL('image/png');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        if (value || old) {
          const img = new Image();
          img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
          img.src = value || old;
        }
      }
    }
    return { rect, dpr };
  }, [value]);

  const syncImage = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    onChange?.(canvas.toDataURL('image/png'));
  }, [onChange]);

  const pointFromEvent = useCallback((event) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const touch = event.touches?.[0] || event.changedTouches?.[0];
    const clientX = touch ? touch.clientX : event.clientX;
    const clientY = touch ? touch.clientY : event.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  }, []);

  const begin = useCallback((event) => {
    event.preventDefault?.();
    resizeCanvas();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawingRef.current = true;
    const point = pointFromEvent(event);
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  }, [pointFromEvent, resizeCanvas]);

  const draw = useCallback((event) => {
    if (!drawingRef.current) return;
    event.preventDefault?.();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const point = pointFromEvent(event);
    ctx.lineTo(point.x, point.y);
    ctx.strokeStyle = '#e0f2fe';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  }, [pointFromEvent]);

  const end = useCallback((event) => {
    if (!drawingRef.current) return;
    event.preventDefault?.();
    drawingRef.current = false;
    syncImage();
  }, [syncImage]);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    onChange?.('');
  }, [onChange]);

  useEffect(() => {
    resizeCanvas();
    const onResize = () => resizeCanvas();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [resizeCanvas]);

  return (
    <div className={`link-driver-signature ${value ? 'signed' : ''}`}>
      <div className="link-driver-signature-head">
        <span>Customer signature</span>
        <button type="button" onClick={clear}>Clear</button>
      </div>
      <canvas
        ref={canvasRef}
        onMouseDown={begin}
        onMouseMove={draw}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={begin}
        onTouchMove={draw}
        onTouchEnd={end}
      />
      <small>{value ? 'Signature captured.' : 'Customer signs here before delivery photo.'}</small>
    </div>
  );
}

function DriverOrderCard({ order, session, orgId, user, tab = 'ready', initiallyOpen = false, onStarted, onDelivered }) {
  const [busy, setBusy] = useState(false);
  const open = true;
  const [acceptingPhoto, setAcceptingPhoto] = useState(false);
  const [routePreview, setRoutePreview] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [deliveringPhoto, setDeliveringPhoto] = useState(false);
  const [receiverName, setReceiverName] = useState('');
  const [note, setNote] = useState('');
  const [signatureData, setSignatureData] = useState('');
  const [error, setError] = useState('');
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const currentStep = step(order, session);
  const actor = actorFor(session, user);
  const timing = order.timings || order.raw?.timings || {};
  const readyMins = minsSince(timing.readyForDriverAt || timing.packedAt || order.updatedAt || order.placedAt || order.createdAt);
  const runMins = myRunMins(order);
  const showRunTimer = tab === 'mine';
  const showReadyTimer = tab === 'ready';

  async function run(fn) {
    setBusy(true);
    setError('');
    try { await fn(); } catch (err) { console.error('[Link Driver] action failed', err); setError(err?.message || 'Action failed. Please try again.'); }
    finally { setBusy(false); }
  }



  async function previewRoute() {
    setError('');
    if (routePreview?.embedUrl) {
      setRoutePreview(null);
      return;
    }
    const destination = order.address || order.raw?.address || '';
    if (!destination) return setError('Customer address is missing, so Maps cannot preview the route.');
    setRouteLoading(true);
    const origin = await getBrowserPosition();
    setRouteLoading(false);
    setRoutePreview({
      embedUrl: buildMapsEmbedUrl(destination, origin),
      externalUrl: buildMapsUrl(destination, origin),
    });
  }

  async function requestRun() {
    await run(async () => {
      await driverRequestRun({ orgId, orderId: order.id, driverId: session.profileId, driverName: session.profileName, actor });
    });
  }

  async function startRunWithPhoto(photoFile) {
    if (!photoFile) return;
    setAcceptingPhoto(true);
    await run(async () => {
      const location = await getBrowserPosition();
      await driverStartRun({ file: photoFile, orgId, orderId: order.id, driverId: session.profileId, driverName: session.profileName, actor, location });
    });
    setAcceptingPhoto(false);
    onStarted?.(order.id);
  }

  async function cancelRunFromDriver() {
    await run(async () => {
      await storeCancelOrder({ orgId, orderId: order.id, actor: { ...actor, role: 'driver' } });
    });
    setCancelConfirmOpen(false);
  }

  async function deliverWithPhoto(photoFile) {
    if (!receiverName.trim() || !signatureData) {
      setError('Receiver name and customer signature are required before taking the delivery photo.');
      return;
    }
    if (!photoFile) return;
    setDeliveringPhoto(true);
    await run(async () => {
      await markDeliveredWithProof({ file: photoFile, orgId, orderId: order.id, driverId: session.profileId, driverName: session.profileName, actor, receiverName: receiverName.trim(), note, signatureData });
    });
    setDeliveringPhoto(false);
    onDelivered?.(order.id);
  }

  return (
    <article className={`link-pwa-order-card link-driver-run ${open ? 'open' : ''}`}>
      <div className="link-pwa-order-top link-driver-order-static">
        <div className="link-driver-order-main-copy">
          <span className="link-pwa-stage">{currentStep === 'ready' ? 'Ready run' : currentStep === 'requested' ? 'Waiting for site' : currentStep === 'selected_waiting_collection' ? 'Selected · waiting for collection photo' : currentStep === 'selected' ? 'Selected by site' : currentStep === 'delivering' ? 'Out for delivery' : 'Delivered'}</span>
          <h2>{orderRef(order)}</h2>
          <p>{customerName(order)} · {storeName(order)}</p>
          <p className="link-driver-address-line">{order.address || order.raw?.address || order.suburb || 'Address not set'}</p>
          <button type="button" className="link-driver-map-btn" onClick={previewRoute}>{routeLoading ? 'Loading map…' : routePreview?.embedUrl ? 'Hide map' : 'Map preview'}</button>
        </div>
        <div className="link-driver-card-side">
          {showReadyTimer ? <div className="link-pwa-timer"><small>Ready</small>{fmtMins(readyMins)}</div> : null}
          {showRunTimer ? <div className="link-pwa-timer"><small>My run</small>{fmtMins(runMins)}</div> : null}
          <strong>{money(finalTotal(order))}</strong>
        </div>
      </div>

      {open ? (
        <div className="link-driver-expanded">
          <div className="link-driver-order-info-grid">
            <div><span>Pickup site</span><p>{storeName(order)}</p></div>
            <div><span>Customer</span><p>{customerName(order)} · {order.customerPhone || order.raw?.customer?.phone || 'No phone'}</p></div>
            <div><span>Payment</span><p>{order.paymentMethod || 'Not set'}</p></div>
            <div><span>Final total</span><p>{money(finalTotal(order))}</p></div>
          </div>

          {routePreview?.embedUrl ? (
            <div className="link-driver-map-preview">
              <div className="link-driver-map-preview-head">
                <span>Route preview</span>
                <a href={routePreview.externalUrl} target="_blank" rel="noreferrer">Open full map</a>
              </div>
              <iframe title={`Route preview ${order.id}`} src={routePreview.embedUrl} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              <small>Google Maps shows live route options, distance, and drive time inside the preview.</small>
            </div>
          ) : null}

          {Array.isArray(order.items) && order.items.length ? (
            <div className="link-driver-pack-mini">
              <span>Pack check</span>
              {order.items.slice(0, 5).map((item, idx) => <p key={`${item.lineId || item.name}-${idx}`}>{Number(item.qty || 1)} × {item.name}</p>)}
              {order.items.length > 5 ? <p>+{order.items.length - 5} more items</p> : null}
            </div>
          ) : null}

          {error ? <div className="link-pwa-alert">{error}</div> : null}

          <div className="link-pwa-actions link-driver-actions">
            {currentStep === 'ready' ? (
              <button type="button" className="primary link-driver-camera-accept" onClick={requestRun} disabled={busy}>
                {busy ? 'Accepting…' : 'Accept run'}
              </button>
            ) : null}
            {currentStep === 'requested' ? (
              <div className="link-pwa-soft-note link-driver-waiting-site">Accepted · waiting for the site to select you.</div>
            ) : null}
            {currentStep === 'selected_waiting_collection' ? (
              <div className="link-pwa-soft-note link-driver-waiting-site">Selected by site · waiting for the store to capture collected proof photo.</div>
            ) : null}
            {currentStep === 'selected' ? (
              <label className={`primary link-driver-camera-accept ${busy ? 'disabled' : ''}`}>
                {acceptingPhoto || busy ? 'Starting run…' : 'Start run · photo required'}
                <input type="file" accept="image/*" capture="environment" disabled={busy} onChange={(e) => startRunWithPhoto(e.target.files?.[0] || null)} />
              </label>
            ) : null}
            {currentStep === 'delivering' ? (
              <div className="link-driver-proof-box link-driver-deliver-box">
                <button type="button" className="danger link-driver-cancel-order" onClick={() => setCancelConfirmOpen(true)} disabled={busy}>Cancel order</button>
                <label>Delivery proof</label>
                <input value={receiverName} onChange={(e) => setReceiverName(e.target.value)} placeholder="Receiver name / handed to" />
                <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Driver note / handover detail (optional)" />
                <SignaturePad value={signatureData} onChange={setSignatureData} />
                <label className={`primary link-driver-camera-accept link-driver-delivered-pill ${(busy || !receiverName.trim() || !signatureData) ? 'disabled' : ''}`}>
                  {deliveringPhoto || busy ? 'Saving delivery…' : 'Delivered · photo required'}
                  <input type="file" accept="image/*" capture="environment" disabled={busy || !receiverName.trim() || !signatureData} onChange={(e) => deliverWithPhoto(e.target.files?.[0] || null)} />
                </label>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      {cancelConfirmOpen ? (
        <div className="link-pwa-modal-backdrop">
          <div className="link-pwa-modal-card">
            <h3>Cancel this order?</h3>
            <p>This will notify Link and move the order into cancelled/issue handling.</p>
            <div><button type="button" onClick={() => setCancelConfirmOpen(false)}>Keep order</button><button type="button" className="danger" onClick={cancelRunFromDriver} disabled={busy}>{busy ? 'Cancelling…' : 'Cancel order'}</button></div>
          </div>
        </div>
      ) : null}
    </article>
  );
}

function DriverBoard({ session, onSwitch }) {
  const { user } = useAuth();
  const { orgId, orders, loading } = useOnTimeLiveData();
  const [activeLane, setActiveLane] = useState(() => {
    if (typeof window === 'undefined') return null;
    const path = window.location.pathname.toLowerCase();
    if ((path.includes('/driver/runs/mine') || path.includes('/link-driver/runs/mine'))) return 'mine';
    if ((path.includes('/driver/runs/delivered') || path.includes('/link-driver/runs/delivered'))) return 'delivered';
    if ((path.includes('/driver/runs/ready') || path.includes('/link-driver/runs/ready'))) return 'ready';
    return null;
  });
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [activeOrderId, setActiveOrderId] = useState('');
  const [overviewPeriod, setOverviewPeriod] = useState('today');
  const [overviewDate, setOverviewDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [isDriverOnline, setIsDriverOnline] = useState(() => readDriverOnline(session));
  const [availabilityBusy, setAvailabilityBusy] = useState(false);
  const [availabilityMessage, setAvailabilityMessage] = useState('');
  const selectedSite = String(session.selectedSite || '').trim();
  const siteLabel = session.selectedSite || (session.sites?.length ? session.sites.join(', ') : 'All assigned sites');
  const allowedSites = new Set((selectedSite ? [selectedSite] : (session.sites || [])).map(String));

  const rows = useMemo(() => {
    const scoped = orders.filter((order) => (!allowedSites.size || allowedSites.has(storeName(order))) && !isCancelledOrder(order));
    const visible = scoped.filter((order) => !isClosedDriverOrder(order));
    const ready = isDriverOnline ? visible
      .filter((order) => readyForDriver(order) && !String(order.stage || order.raw?.stage || '').toLowerCase().includes('out_for_delivery') && !isAssignedToOther(order, session))
      .sort((a, b) => (safeTime(readyTime(a)) || 0) - (safeTime(readyTime(b)) || 0)) : [];
    const mine = visible.filter((order) => String(order.stage || order.raw?.stage || '').toLowerCase() === 'out_for_delivery' && isAssignedTo(order, session));
    const delivered = scoped.filter((order) => String(order.stage || order.raw?.stage || '').toLowerCase() === 'delivered' && isAssignedTo(order, session));
    return { scoped, visible, ready, mine, delivered };
  }, [orders, session, isDriverOnline]);

  const driverOverviewRange = useMemo(() => periodRange(overviewPeriod, overviewDate), [overviewPeriod, overviewDate]);
  const deliveredInSelectedPeriod = useMemo(() => rows.delivered.filter((order) => (
    isAssignedTo(order, session)
    && isInRange(deliveredTime(order), driverOverviewRange)
  )), [rows.delivered, session, driverOverviewRange]);

  const laneKey = activeLane || 'ready';
  const list = laneKey === 'mine' ? rows.mine : laneKey === 'delivered' ? deliveredInSelectedPeriod : rows.ready;
  const myRunAvg = rows.mine.length ? Math.round(rows.mine.reduce((sum, order) => sum + myRunMins(order), 0) / rows.mine.length) : 0;

  const overview = useMemo(() => buildDriverIntelligence({ rows, deliveredInSelectedPeriod, driverOverviewRange, session }), [driverOverviewRange, rows, session, deliveredInSelectedPeriod]);
  const laneTitle = activeLane === 'mine' ? 'My run' : activeLane === 'delivered' ? 'Delivered' : 'Ready';
  const laneSub = activeLane === 'mine' ? 'active runs' : activeLane === 'delivered' ? 'completed by me' : 'waiting for driver';

  const handleRunStarted = useCallback((orderId = '') => {
    setActiveOrderId(orderId || '');
    setActiveLane('mine');
    if (typeof window !== 'undefined') {
      const base = window.location.pathname.startsWith('/link-driver') ? '/link-driver' : '/driver';
      window.history.replaceState({}, '', `${base}/runs/mine`);
    }
  }, []);

  const handleRunDelivered = useCallback(() => {
    setActiveOrderId('');
    setActiveLane(null);
    if (typeof window !== 'undefined') {
      const base = window.location.pathname.startsWith('/link-driver') ? '/link-driver' : '/driver';
      window.history.replaceState({}, '', base);
    }
  }, []);

  useEffect(() => {
    if (!activeLane || loading) return;
    if (activeLane === 'ready' && activeOrderId && !rows.ready.some((order) => String(order.id) === String(activeOrderId))) {
      handleRunStarted(activeOrderId);
    }
    if (activeLane === 'mine' && activeOrderId && !rows.mine.some((order) => String(order.id) === String(activeOrderId))) {
      handleRunDelivered();
    }
  }, [activeLane, activeOrderId, rows.ready, rows.mine, loading, handleRunStarted, handleRunDelivered]);


  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onPop = () => {
      const path = window.location.pathname.toLowerCase();
      if ((path.includes('/driver/runs/mine') || path.includes('/link-driver/runs/mine'))) setActiveLane('mine');
      else if ((path.includes('/driver/runs/delivered') || path.includes('/link-driver/runs/delivered'))) setActiveLane('delivered');
      else if ((path.includes('/driver/runs/ready') || path.includes('/link-driver/runs/ready'))) setActiveLane('ready');
      else setActiveLane(null);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const goDriverHome = useCallback(() => {
    setActiveLane(null);
    if (typeof window !== 'undefined') { const base = window.location.pathname.startsWith('/link-driver') ? '/link-driver' : '/driver'; if (window.location.pathname !== base) window.history.pushState({}, '', base); }
  }, []);

  const openDriverLane = useCallback((lane) => {
    setActiveLane(lane);
    if (typeof window !== 'undefined') { const base = window.location.pathname.startsWith('/link-driver') ? '/link-driver' : '/driver'; window.history.pushState({}, '', `${base}/runs/${lane}`); }
  }, []);

  useEffect(() => {
    setIsDriverOnline(readDriverOnline(session));
  }, [session?.orgId, session?.profileId]);

  useEffect(() => {
    if (!session?.profileId || !orgId || !isDriverOnline) return undefined;
    let watchId = null;
    let stopped = false;
    const pushLocation = (pos) => {
      if (stopped || !pos?.coords) return;
      updateDriverLocation({
        orgId,
        driverId: session.profileId,
        driverName: session.profileName,
        location: { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy || null },
      }).catch((err) => console.warn('[Link Driver] location update failed', err));
    };
    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(pushLocation, () => {}, { enableHighAccuracy: true, timeout: 9000, maximumAge: 30000 });
      watchId = navigator.geolocation.watchPosition(pushLocation, () => {}, { enableHighAccuracy: true, timeout: 15000, maximumAge: 20000 });
    }
    return () => {
      stopped = true;
      if (watchId != null && navigator?.geolocation) navigator.geolocation.clearWatch(watchId);
    };
  }, [orgId, session?.profileId, session?.profileName, isDriverOnline]);

  // Restore driver online preference after refresh and keep Firestore aligned.
  // Local storage is the source of truth for the PWA session; Firestore is used as a fallback
  // so the driver does not silently fall Offline when the app reloads.
  useEffect(() => {
    let cancelled = false;
    async function restoreOnlineState() {
      const stored = storedDriverOnlineValue(session);
      if (stored !== null) {
        if (!cancelled) setIsDriverOnline(stored);
        if (session?.profileId && orgId) {
          setDriverAvailability({ orgId, driverId: session.profileId, driverName: session.profileName, site: selectedSite || siteLabel, status: stored ? 'online' : 'offline' }).catch(() => {});
        }
        return;
      }
      const saved = readDriverOnline(session);
      if (saved) {
        if (!cancelled) setIsDriverOnline(true);
        if (session?.profileId && orgId) {
          setDriverAvailability({ orgId, driverId: session.profileId, driverName: session.profileName, site: selectedSite || siteLabel, status: 'online' }).catch(() => {});
        }
        return;
      }
      if (session?.profileId && orgId) {
        try {
          const snap = await getDoc(doc(db, 'orgs', orgId, 'ontimeDrivers', session.profileId));
          const status = String(snap.data()?.status || '').toLowerCase();
          if (!cancelled && status === 'online') {
            writeDriverOnline(session, true);
            setIsDriverOnline(true);
          }
        } catch (_) {}
      }
    }
    restoreOnlineState();
    return () => { cancelled = true; };
  }, [orgId, session?.profileId]);

  async function toggleDriverAvailability() {
    if (!session?.profileId || availabilityBusy) return;
    setAvailabilityBusy(true);
    setAvailabilityMessage('');
    const nextOnline = !isDriverOnline;
    try {
      const location = nextOnline ? await getBrowserPosition() : null;
      await setDriverAvailability({
        orgId,
        driverId: session.profileId,
        driverName: session.profileName,
        site: selectedSite || siteLabel,
        status: nextOnline ? 'online' : 'offline',
        location,
      });
      writeDriverOnline(session, nextOnline);
      setIsDriverOnline(nextOnline);
      if (!nextOnline && activeLane === 'ready') setActiveLane(null);
      setAvailabilityMessage(nextOnline
        ? ''
        : 'You are offline. Ready runs are hidden and live tracking is paused.');
    } catch (err) {
      console.error('[Link Driver] availability update failed', err);
      setAvailabilityMessage('Could not update driver status. Please try again.');
    } finally {
      setAvailabilityBusy(false);
      if (nextOnline) window.setTimeout(() => setAvailabilityMessage(''), 4500);
    }
  }

  async function performLogout() {
    if (isDriverOnline && session?.profileId && orgId) {
      try {
        await setDriverAvailability({ orgId, driverId: session.profileId, driverName: session.profileName, site: selectedSite || siteLabel, status: 'offline' });
      } catch (_) {}
      writeDriverOnline(session, false);
      setIsDriverOnline(false);
    }
    clearDriverSession();
    try { await signOut(getAuthClient()); } catch (_) {}
  }

  function requestLogout() {
    if (isDriverOnline) setLogoutConfirmOpen(true);
    else performLogout();
  }

  const currentRun = rows.mine[0] || null;
  const scheduleRows = [...rows.ready].sort((a, b) => (safeTime(readyTime(a)) || 0) - (safeTime(readyTime(b)) || 0)).slice(0, 6);
  const todayRange = periodRange('today');
  const deliveredToday = rows.delivered.filter((order) => isAssignedTo(order, session) && isInRange(deliveredTime(order), todayRange));
  const earningsToday = deliveredToday.reduce((sum, order) => sum + finalTotal(order), 0);
  const initials = profileName({ displayName: session.profileName }).split(' ').map((x) => x[0]).join('').slice(0, 2).toUpperCase() || 'DR';

  return (
    <div className="link-pwa-screen link-driver-pwa link-driver-native">
      {!activeLane ? (
        <>
          <header className="link-driver-native-top">
            <button type="button" className="link-store-menu-dot" onClick={() => setMenuOpen((v) => !v)}>☰</button>
            <div><LinkLogo small /><div className="link-pwa-kicker">Driver</div></div>
            <button type="button" className="link-store-bell">🔔{rows.ready.length ? <em>{rows.ready.length}</em> : null}</button>
          </header>
          {menuOpen ? (
            <div className="link-driver-menu-panel" onClick={(e) => e.stopPropagation()}>
              <button type="button" onClick={() => { setMenuOpen(false); onSwitch(); }}>Switch Site</button>
              <button type="button" onClick={() => setMenuOpen(false)}><b>About Link Driver</b><small>Version 1.0 · 2026</small></button>
              <button type="button" onClick={() => setMenuOpen(false)}><b>Help</b><small>Contact your Link admin for PINs, site access, and run issues.</small></button>
              <button type="button" onClick={requestLogout}>Logout</button>
            </div>
          ) : null}
          {menuOpen ? <button type="button" aria-label="Close menu" className="link-driver-menu-scrim" onClick={() => setMenuOpen(false)} /> : null}

          <section className="link-driver-profile-card">
            <div className="link-driver-avatar-wrap"><div className="link-driver-avatar">{initials}</div><i className={isDriverOnline ? 'online' : 'offline'} /></div>
            <div className="link-driver-profile-main">
              <h1 title={session.profileName}>{session.profileName}</h1>
              <button type="button" className={`link-driver-status-line ${isDriverOnline ? 'online' : 'offline'}`} onClick={toggleDriverAvailability} disabled={availabilityBusy}>{availabilityBusy ? 'Updating…' : isDriverOnline ? 'Online' : 'Offline'}</button>
              <p>{siteLabel}</p>
              <small>★ 0 driver score</small>
            </div>
            <button type="button" className="link-driver-earnings-tile" onClick={() => setOverviewOpen((v) => !v)}>
              <span>Today's deliveries</span>
              <strong>{deliveredToday.length}</strong>
              <small>{money(earningsToday)} delivered today</small>
            </button>
            <div className="link-driver-stat-strip">
              <button type="button" onClick={() => openDriverLane('mine')}><i>🚙</i><strong>{rows.mine.length}</strong><small>Active run</small></button>
              <button type="button" onClick={() => openDriverLane('ready')}><i>⏱️</i><strong>{fmtMins(overview.avgAcceptance)}</strong><small>ETA to drop</small></button>
              <button type="button"><i>🗺️</i><strong>—</strong><small>Distance left</small></button>
              <button type="button"><i>🔋</i><strong>—</strong><small>Battery</small></button>
            </div>
          </section>

          {!isDriverOnline ? <div className="link-driver-availability-message">{availabilityMessage || 'You are offline. Tap Go Online to start receiving ready runs.'}</div> : availabilityMessage ? <div className="link-driver-availability-message">{availabilityMessage}</div> : null}

          <section className={`link-driver-current-run ${currentRun ? 'active' : ''}`}>
            <div className="link-driver-section-head"><span>Current run</span>{currentRun ? <small>{fmtMins(myRunMins(currentRun))}</small> : null}</div>
            {currentRun ? (
              <button type="button" className="link-driver-current-card" onClick={() => openDriverLane('mine')}>
                <div><h2>{orderRef(currentRun)}</h2><p>{customerName(currentRun)}</p><small>📍 {storeName(currentRun)}</small></div>
                <div><strong>{money(finalTotal(currentRun))}</strong><small>{currentRun.paymentMethod || 'Cash on delivery'}</small></div>
              </button>
            ) : <div className="link-store-empty">No active run yet.</div>}
          </section>

          <section className="link-driver-schedule-card">
            <div className="link-driver-section-head"><span>Ready Runs</span><small>{scheduleRows.length} runs</small></div>
            {loading ? <div className="link-store-empty">Loading driver board…</div> : null}
            {!loading && !scheduleRows.length ? <div className="link-store-empty">No ready runs right now.</div> : null}
            {scheduleRows.map((order) => {
              const state = String(order.stage || '').toLowerCase() === 'delivered' ? 'Delivered' : isAssignedTo(order, session) ? 'In progress' : 'Ready';
              return (
                <button type="button" key={order.id} className={`link-driver-schedule-row ${state.toLowerCase().replace(' ', '-')}`} onClick={() => { setActiveOrderId(order.id); openDriverLane(isAssignedTo(order, session) ? 'mine' : state === 'Delivered' ? 'delivered' : 'ready'); }}>
                  <i>{state === 'Delivered' ? '✓' : state === 'In progress' ? '○' : '•'}</i>
                  <div><b>{orderRef(order)}</b><small>{state}</small><p>{storeName(order)}</p><em>Arrived {formatClock(readyTime(order))} · Waiting {readyWaitLabel(order)}</em></div>
                  <strong>{money(finalTotal(order))}</strong>
                </button>
              );
            })}
          </section>

          {overviewOpen ? (
            <section className="link-driver-performance-grid">
              <div><span>Performance</span><strong>{overview.rate}%</strong><small>Acceptance</small></div>
              <div><span>Completed</span><strong>{overview.deliveredCount}</strong><small>{overview.range.label}</small></div>
              <div><span>Avg run</span><strong>{fmtMins(overview.avgDelivery)}</strong><small>Accepted to delivered</small></div>
            </section>
          ) : null}

          <nav className="link-store-bottom-nav link-driver-bottom-nav">
            <button type="button" className="active"><i>⌂</i><span>Home</span></button>
            <button type="button" onClick={() => openDriverLane('ready')}><i>☷</i><span>Runs</span></button>
            <button type="button" className={`scan ${isDriverOnline ? 'online' : 'offline'}`} onClick={toggleDriverAvailability} disabled={availabilityBusy}><i>⏻</i><span>{isDriverOnline ? 'Go Offline' : 'Go Online'}</span></button>
            <button type="button" onClick={() => setOverviewOpen((v) => !v)}><i>▣</i><span>Performance</span></button>
            <button type="button" onClick={() => setMenuOpen((v) => !v)}><i>•••</i><span>More</span></button>
          </nav>
          {logoutConfirmOpen ? (
            <div className="link-pwa-modal-backdrop">
              <div className="link-pwa-modal-card">
                <h3>Go offline and logout?</h3>
                <p>You are currently Online. Logging out will automatically set you Offline and stop live tracking.</p>
                <div><button type="button" onClick={() => setLogoutConfirmOpen(false)}>Cancel</button><button type="button" className="danger" onClick={performLogout}>Logout</button></div>
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <main className="link-driver-lane-page">
          <section className="link-driver-lane-head">
            <button type="button" className="link-pwa-ghost link-driver-back-btn" onClick={() => { setActiveOrderId(''); goDriverHome(); }}>← Back</button>
            <div>
              <span>{activeLane === 'mine' ? 'My run' : activeLane === 'delivered' ? 'Delivered' : 'Ready runs'}</span>
              <h1>{laneTitle}</h1>
              <p>{list.length} {list.length === 1 ? 'order' : 'orders'} for {siteLabel}.</p>
            </div>
          </section>
          <section className="link-pwa-order-stack link-driver-lane-stack">
            {loading ? <div className="link-pwa-empty">Loading driver board…</div> : null}
            {!loading && !list.length ? <div className="link-pwa-empty">{activeLane === 'ready' && !isDriverOnline ? 'Go online to view ready runs.' : `No ${laneSub} right now.`}</div> : null}
            {list.filter((order) => !activeOrderId || String(order.id) === String(activeOrderId)).map((order) => <DriverOrderCard key={order.id} order={order} session={session} orgId={orgId} user={user} tab={laneKey} initiallyOpen={Boolean(activeOrderId)} onStarted={handleRunStarted} onDelivered={handleRunDelivered} />)}
          </section>
        </main>
      )}
    </div>
  );
}

export default function LinkDriverPwaPage() {
  const { user, claims, loading } = useAuth();
  const [session, setSession] = useState(() => readDriverSession());
  const [emailPassed, setEmailPassed] = useState(false);


  const resolvedOrgId = String(claims?.orgId || session?.orgId || '').trim();
  const hasSavedDriverSession = Boolean(session?.orgId && session?.profileId);
  const hasAccess = hasOnTimeAccess(claims || {}) || hasSavedDriverSession;

  if (loading) return <div className="link-pwa-screen link-driver-pwa link-pwa-centered"><div className="link-pwa-empty link-pwa-loading-pop">Loading Link Driver…</div></div>;
  if (!hasSavedDriverSession && (!user || !hasAccess || !emailPassed)) return <DriverEmailLogin onReady={() => { clearDriverSession(); setSession(null); setEmailPassed(true); }} />;
  if (!session?.profileId || String(session?.orgId || '') !== resolvedOrgId) return <DriverProfileGate onUnlock={setSession} />;
  return <DriverBoard session={session} onSwitch={() => { clearDriverSession(); setSession(null); setEmailPassed(true); }} />;
}
